<template>
  <v-layout
    align-center="true"
    class="reverse-geocoding-field"
  >
    <v-layout
      v-if="resolvedAddress && !loading"
      :class="textClass"
    >
      <div>
        {{ resolvedAddress }}
      </div>
    </v-layout>
    <v-layout
      v-if="loading"
      :class="loaderClass"
    >
      <v-progress-circular
        align-center
        class="ml-2"
        indeterminate
        width="2"
        size="15"
      />
    </v-layout>
  </v-layout>
</template>

<script>
import { api } from '@/global/services/api'
import { isFunction, isObject, isSet, isString } from '@/global/utils/utils'

const DEFAULT_TEXT_CLASS = 'justify-start'
const DEFAULT_LOADER_CLASS = 'justify-start'
export default {
  name: 'ReverseGeocodePreview',

  props: {
    loopRetry: {
      type: Boolean,
      default: false
    },
    retry: {
      type: Boolean,
      default: false
    },
    index: {
      type: Number,
      default: null
    },
    config: {
      type: Object,
      default: () => ({})
    },
    rowIndex: {
      type: Number,
      default: null
    },
    rowData: {
      type: Object,
      default: () => ({})
    }
  },

  data () {
    return {
      loading: true,
      localResolvedAddress: null
    }
  },

  computed: {
    computedConfig () {
      return this.config
    },

    batchGeoreverseResult () {
      const { batchGeoreverseResult } = this.computedConfig || {}
      return isFunction(batchGeoreverseResult) ? batchGeoreverseResult() : batchGeoreverseResult
    },

    batchReverseGeocode () {
      return !!this.computedConfig?.batchReverseGeocode
    },

    params () {
      const { params } = this.computedConfig || {}
      if (!isSet(params)) {
        return null
      }
      if (isFunction(params)) {
        return params({
          data: this.rowData
        })
      }

      return params
    },

    setCoordinatesInQueue () {
      const { setCoordinatesInQueue } = this.computedConfig || {}
      return isFunction(setCoordinatesInQueue) ? setCoordinatesInQueue : null
    },

    resolvedAddress () {
      return this.batchReverseGeocode
        ? this.batchGeoreverseResult?.[this.rowIndex] || null
        : this.localResolvedAddress
    },

    hasBatchResultForRow () {
      if (!this.batchReverseGeocode || !isSet(this.rowIndex) || !isObject(this.batchGeoreverseResult)) {
        return false
      }

      return Object.prototype.hasOwnProperty.call(this.batchGeoreverseResult, this.rowIndex)
    },

    textClass () {
      const { textClass = DEFAULT_TEXT_CLASS } = this.computedConfig || {}
      if (isString(textClass)) {
        return textClass
      }

      return DEFAULT_TEXT_CLASS
    },

    loaderClass () {
      const { loaderClass = DEFAULT_LOADER_CLASS } = this.computedConfig || {}
      if (isString(loaderClass)) {
        return loaderClass
      }

      return DEFAULT_LOADER_CLASS
    }
  },

  watch: {
    params: {
      immediate: true,
      deep: true,
      handler (val) {
        if (!isSet(val) || !isObject(val)) {
          return
        }

        this.loading = true

        if (!this.batchReverseGeocode) {
          this.resolveAddress(false)
          return
        }

        this.setCoordinatesInQueue?.({
          data: this.rowData,
          rowIndex: this.rowIndex
        })
      }
    },

    resolvedAddress: {
      immediate: true,
      handler (val) {
        if (isSet(val)) {
          this.loading = false
        }
      }
    },

    hasBatchResultForRow: {
      immediate: true,
      handler (val) {
        if (val) {
          this.loading = false
        }
      }
    }
  },

  methods: {
    async resolveAddress (isRetry) {
      this.localResolvedAddress = null
      this.loading = true

      try {
        const response = await api()['satellite-tracking'].get('reverse-geocode', this.params)

        this.$nextTick(() => {
          if (this.shouldTryAgain(response, isRetry)) {
            this.resolveAddress(true)
          }
          else {
            this.localResolvedAddress = response.data.address
          }
        })
      }
      catch (error) {
        console.error(error)
      }
      finally {
        this.loading = false
      }
    },

    // Handle retry logic
    shouldTryAgain (response, isRetry) {
      if (!response.data.address && !isRetry && this.retry) {
        return true
      }

      return !response.data.address && this.loopRetry
    }
  }
}

</script>
